import type { CatalogItem, SearchHit, SearchQuery } from '@viva/core';
import type { SportsMatch } from '../account/api';
import { categoryTags } from '../category-tags';
import { clubChannelOf, sameTeam, teamWords } from '../team-names';
import { channelFamily } from './match-qualities';

/**
 * "More channels to try" under a match's channels (Abel, 2026-10-31). The relay's
 * tests can miss channels that work, so the panel always offers a few more from the
 * viewer's own channel list: the league's usual channels ("Premier League+ 3",
 * "Sky Sports+ 2", "ES| LA LIGA 1") and both clubs' 24/7 channels. Not checked.
 */

/** At most this many extra channels per match (Abel). */
export const MAX_EXTRA_CHANNELS = 6;

interface LeagueFeed {
  /** What to ask the catalog search for. */
  query: string;
  /** A name the search found must also match this (the search tolerates typos). */
  pattern: RegExp;
  /** Names that match the pattern but are something else. */
  exclude?: RegExp;
}

/** Channels that usually carry a league's matches, by the league heading the match is listed under. */
export const LEAGUE_FEEDS: Record<string, LeagueFeed[]> = {
  'Premier League': [
    { query: 'premier league', pattern: /premier league\s*\+/i },
    { query: 'sky sports', pattern: /sky sports?\s*\+/i },
  ],
  // "ES| LA LIGA 1 FHD", "ES| DAZN LA LIGA 3", "SUPERSPORT LA LIGA", "LALIGA TV"; Movistar+ in Spain, ESPN+ in the US.
  LaLiga: [
    { query: 'la liga', pattern: /\bla ?liga\b/i, exclude: /\bla ?liga (2|hypermotion|f)\b/i },
    { query: 'laliga', pattern: /\bla ?liga\b/i, exclude: /\bla ?liga (2|hypermotion|f)\b/i },
    { query: 'movistar', pattern: /\bmovistar\b.*\b(la ?liga|liga|futbol|fútbol|deportes)\b/i },
    { query: 'espn', pattern: /\bespn\s*\+/i },
  ],
  'Serie A': [{ query: 'serie a', pattern: /\bserie a\b/i, exclude: /\bfemminile\b|\bwomen\b/i }],
  // "DE| SKY BUNDESLIGA 1 HD", "DE| BUNDESLIGA (DAZN …)".
  Bundesliga: [{ query: 'bundesliga', pattern: /\bbundesliga\b/i, exclude: /\b2\.?\s*bundesliga\b|\bfrauen\b/i }],
  // "Ligue 1 | Match 1".
  'Ligue 1': [{ query: 'ligue 1', pattern: /\bligue 1\b/i }],
  // "PRIME| UEFA CHAMPIONS LEAGUE".
  'Champions League': [{ query: 'champions league', pattern: /\bchampions league\b/i, exclude: /\b(afc|caf|concacaf|women|uwcl)\b/i }],
};

/** Not live football: replays, highlights, news. */
const LEAVE_OUT = /\b(replay|highlights?|news|magazine|classics?|radio)\b/i;
const RAW = /\bRAW\b|ᴿᴬᵂ/i;
const HEVC = /\bhevc\b|\bh\.?265\b|ᴴᴱⱽᶜ/i;

/** "Levante vs. Atletico Madrid", "Real Madrid v Barcelona". */
const VERSUS = /\s(?:vs?\.?|versus)\s/i;
/** A date or clock time in an event channel's name: "(2026-10-31 08:48)", "31/10", "20:00". */
const WHEN = /\b\d{4}-\d{2}-\d{2}\b|\b\d{1,2}[./]\d{1,2}(?:[./]\d{2,4})?\b|\b\d{1,2}:\d{2}\b/;

/**
 * Whether a channel named after one match is about another match. Event channels such as
 * "CA (TSN+ 024) | LaLiga: Levante vs. Atletico Madrid (2026-10-31 08:48)" carry the
 * league's name, so they passed the LaLiga pattern for every LaLiga match (Abel, 2026-11-01).
 * A name with "vs" is kept only when both sides are this match's teams; a name with a date
 * or time but no teams can't be placed and is left out.
 */
export function namesOtherMatch(name: string, home: string, away: string): boolean {
  const at = name.search(VERSUS);
  if (at < 0) return WHEN.test(name);
  const left = name.slice(0, at).split(/[|:]/).pop() ?? '';
  const right = name
    .slice(at)
    .replace(VERSUS, '')
    .split(/[([|]|\s[-–]\s/)[0]!
    .replace(WHEN, '');
  return !((sameTeam(left, home) && sameTeam(right, away)) || (sameTeam(left, away) && sameTeam(right, home)));
}

/** Which quality to offer when a channel is listed in several: HD first, as in the match list. */
function qualityRank(name: string): number {
  if (RAW.test(name) || HEVC.test(name)) return 6;
  if (/\bfhd\b|ᶠᴴᴰ|\b1080p?\b/i.test(name)) return 1;
  if (/\b(uhd|4k|8k)\b|ᵁᴴᴰ/i.test(name)) return 4;
  if (/\bsd\b|ˢᴰ/i.test(name)) return 3;
  if (/\bhd\b|ᴴᴰ|\b720p?\b/i.test(name)) return 0;
  return 2;
}

/** The league a match is listed under, for older servers without `league` too. */
function leagueOf(match: SportsMatch): string {
  if (match.league && LEAGUE_FEEDS[match.league]) return match.league;
  const competition = match.competition.toLowerCase();
  if (/\bchampions league\b/.test(competition) && !/\b(afc|caf|women|youth)\b/.test(competition)) return 'Champions League';
  return match.league ?? '';
}

/** The catalog searches to run for a match: the league's channels, then each club by its longest name word. */
export function extraChannelQueries(match: SportsMatch): string[] {
  const league = (LEAGUE_FEEDS[leagueOf(match)] ?? []).map((feed) => feed.query);
  // One word finds "NEWCASTLE UTD" for "Newcastle United" and "ATLETICO MADRID" for "Atlético de Madrid".
  const clubs = [match.home, match.away].flatMap((team) => {
    const words = teamWords(team).filter((word) => word.length >= 3 && !/^(women|youth|u\d{2})$/.test(word));
    const longest = words.sort((a, b) => b.length - a.length)[0];
    return longest ? [longest] : [];
  });
  return [...new Set([...league, ...clubs])];
}

/** Families of the channels the match already lists (tested or "might have"), so they aren't offered twice. */
function listedFamilies(match: SportsMatch): Set<string> {
  const names = [...match.channels.flatMap((channel) => [channel.label, ...(channel.variants ?? []).map((variant) => variant.label)]), ...(match.upcoming ?? []).map((channel) => channel.label)];
  return new Set(names.map(channelFamily));
}

/**
 * Up to `max` channels from the viewer's list to try for a match: the clubs' own
 * channels first (home team's first), then the viewer's commentary languages, then
 * by name ("Premier League+ 2" before "Premier League+ 10"). One quality per channel.
 */
export function pickExtraChannels(match: SportsMatch, candidates: CatalogItem[], preferred: string[], max = MAX_EXTRA_CHANNELS): CatalogItem[] {
  const feeds = LEAGUE_FEEDS[leagueOf(match)] ?? [];
  const listed = listedFamilies(match);
  const teams = [match.home, match.away];
  const best = new Map<string, { item: CatalogItem; club: number; language: string | null }>();
  for (const item of candidates) {
    if (item.kind !== 'live' || !item.url) continue;
    const name = item.name;
    if (LEAVE_OUT.test(name)) continue;
    if (namesOtherMatch(name, match.home, match.away)) continue;
    const club = clubChannelOf(name);
    let clubIndex = -1;
    if (club) {
      clubIndex = teams.findIndex((team) => sameTeam(team, club.club));
      // Another club's channel ("LA LIGA| BARCELONA" for Atlético – Sevilla).
      if (clubIndex < 0) continue;
    } else if (!feeds.some((feed) => feed.pattern.test(name) && !feed.exclude?.test(name))) {
      continue;
    }
    const family = channelFamily(name);
    if (!family || listed.has(family)) continue;
    const current = best.get(family);
    if (current && qualityRank(current.item.name) <= qualityRank(name)) continue;
    best.set(family, { item, club: clubIndex, language: categoryTags(name).language ?? club?.language ?? null });
  }
  const languageRank = (language: string | null) => (language !== null && preferred.includes(language) ? preferred.indexOf(language) : preferred.length);
  const clubRank = (club: number) => (club < 0 ? teams.length : club);
  return [...best.values()]
    .sort(
      (a, b) =>
        clubRank(a.club) - clubRank(b.club) ||
        languageRank(a.language) - languageRank(b.language) ||
        a.item.name.localeCompare(b.item.name, 'en', { numeric: true, sensitivity: 'base' }),
    )
    .slice(0, max)
    .map((entry) => entry.item);
}

/** How many hits each search may return before they are narrowed down. */
const SEARCH_LIMIT = 500;

/** Searches the viewer's channel list for a match's extra channels. */
export async function findExtraChannels(
  search: (query: SearchQuery) => Promise<SearchHit[]>,
  match: SportsMatch,
  preferred: string[],
): Promise<CatalogItem[]> {
  const queries = extraChannelQueries(match);
  const results = await Promise.all(queries.map((query) => search({ query, limit: SEARCH_LIMIT, kinds: ['live'] }).catch(() => [] as SearchHit[])));
  const seen = new Set<string>();
  const candidates: CatalogItem[] = [];
  for (const { item } of results.flat()) {
    const key = `${item.sourceId}:${item.key}`;
    if (seen.has(key)) continue;
    seen.add(key);
    candidates.push(item);
  }
  return pickExtraChannels(match, candidates, preferred);
}

/** Channels to search for, named right before the Search button (Abel, 2026-10-31). */
export const LEAGUE_SEARCH_NAMES: Record<string, string[]> = {
  'Premier League': ['Premier League+', 'Sky Sports+'],
  LaLiga: ['LA LIGA', 'DAZN LaLiga', 'Movistar', 'ESPN+'],
  'Serie A': ['Serie A'],
  Bundesliga: ['Sky Bundesliga', 'DAZN Bundesliga'],
  'Ligue 1': ['Ligue 1'],
  'Champions League': ['Champions League'],
};

/** The channel names to suggest searching for; empty for leagues without known channels. */
export function searchHintNames(match: SportsMatch): string[] {
  return LEAGUE_SEARCH_NAMES[leagueOf(match)] ?? [];
}
